import React, { useState, useEffect, useRef } from 'react';
import projectService from '../../../services/projectService';
import logger from '../../../utils/logger';
import { canFinalize } from '../../../utils/roles';

const SynopsisCard = ({ currentSynopsis, synopsisVersions, selectedVersionIndex, onRefresh, user, readOnly = false }) => {
  const [editedHumanVersionText, setEditedHumanVersionText] = useState('');
  const [isSavingHumanVersion, setIsSavingHumanVersion] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const saveTimeoutRef = useRef(null);
  
  // Initialize human version text when synopsis changes
  useEffect(() => {
    if (currentSynopsis) {
      const humanVersion = currentSynopsis.human_version || currentSynopsis.synopsis_text || currentSynopsis.synopsis || '';
      setEditedHumanVersionText(humanVersion);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSynopsis?.id]);
  
  // Auto-save functionality with debounce
  const handleHumanVersionChange = (newText) => {
    setEditedHumanVersionText(newText);
    
    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Set new timeout for auto-save (1 second after user stops typing)
    saveTimeoutRef.current = setTimeout(() => {
      saveHumanVersion(newText);
    }, 1000);
  };
  
  const saveHumanVersion = async (text) => {
    if (!currentSynopsis?.id) return;
    
    const trimmedText = (text || '').trim();
    
    try {
      setIsSavingHumanVersion(true);
      await projectService.updateHumanVersion(currentSynopsis.id, trimmedText);
      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      logger.error('Error saving human version:', error);
      alert(error?.response?.data?.detail || 'Failed to save human version. Please try again.');
    } finally {
      setIsSavingHumanVersion(false);
    }
  };
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);
  
  if (!currentSynopsis) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-2">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Synopsis</h2>
        <div className="text-center py-8">
          <p className="text-gray-600">No synopsis available yet</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="bg-white dark:bg-neutral-950 rounded-lg p-2">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-neutral-100 mb-4">Synopsis</h2>
      
      {/* Two-column layout: AI Synopsis and Human Version */}
      <div className="grid grid-cols-2 gap-2">
        {/* AI Generated Synopsis */}
        <div className="flex flex-col">
          <div className="flex items-center mb-2">
            <h3 className="text-sm text-gray-900 dark:text-neutral-100 mr-2">AI Generated(gemini-2.0-flash-exp)</h3>
            {synopsisVersions.length > 1 && (
              <span className="px-2 py-0.5 bg-sky-100 dark:bg-sky-900 text-sky-800 dark:text-sky-200 rounded-full text-xs font-medium">
                v{(selectedVersionIndex !== null ? selectedVersionIndex + 1 : synopsisVersions.length)}/{synopsisVersions.length}
              </span>
            )}
          </div>
          <div className="bg-gray-50 dark:bg-neutral-900 rounded-lg p-2 border border-gray-200 dark:border-neutral-700 flex-1">
            <p className="text-sm text-gray-800 dark:text-neutral-200 whitespace-pre-wrap leading-relaxed">
              {currentSynopsis.synopsis_text || currentSynopsis.synopsis || 'No synopsis available'}
            </p>
          </div>
        </div>
        
        {/* Human Version - Always in Edit Mode */}
        <div className="flex flex-col">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <span className={`px-3 py-1 rounded-full text-xs font-light ${
                currentSynopsis?.approval_status === 'approved' 
                  ? 'bg-accent/20 text-accent' 
                  : currentSynopsis?.approval_status === 'rejected'
                  ? 'bg-destructive/15 text-destructive'
                  : 'bg-muted text-muted-foreground'
              }`}>
                {currentSynopsis?.approval_status === 'approved' 
                  ? 'Editing is approved' 
                  : currentSynopsis?.approval_status === 'rejected'
                  ? 'Review is needed'
                  : 'Editing and confirmation needed'}
              </span>
              {canFinalize(user) && currentSynopsis?.approval_status !== 'approved' && (
                <button
                  type="button"
                  disabled={isApproving}
                  onClick={async () => {
                    try {
                      setIsApproving(true);
                      await projectService.approveHumanVersion(currentSynopsis.id);
                      if (onRefresh) await onRefresh();
                    } catch (e) {
                      logger.error('Error approving human version:', e);
                      alert(e?.response?.data?.detail || 'Failed to approve.');
                    } finally {
                      setIsApproving(false);
                    }
                  }}
                  className="px-3 py-1 rounded-md text-xs font-medium bg-accent text-accent-foreground hover:opacity-90 disabled:opacity-70"
                >
                  {isApproving ? 'Approving…' : 'Approve'}
                </button>
              )}
              {!canFinalize(user) && currentSynopsis?.approval_status !== 'approved' && (
                <span className="text-xs text-muted-foreground">Only a reviewer or above can approve.</span>
              )}
            </div>
          </div>
          <div className="bg-gray-50 dark:bg-neutral-900 rounded-lg border border-sky-500 border-2 flex-1">
            <textarea
              value={editedHumanVersionText}
              onChange={(e) => !readOnly && handleHumanVersionChange(e.target.value)}
              readOnly={readOnly}
              disabled={readOnly}
              className="w-full h-full min-h-[200px] p-2 border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-950 rounded text-sm focus:ring-2 focus:ring-sky-500 focus:border-sky-500 text-gray-800 dark:text-neutral-200 leading-relaxed resize-none disabled:opacity-70 disabled:cursor-not-allowed"
              placeholder="Enter human version of synopsis..."
            />
          </div>
          {!readOnly && (
          <div className="flex items-center gap-3 mt-1 text-xs text-gray-600 dark:text-neutral-400">
            Editing will be saved automatically.
            {isSavingHumanVersion && (
                  <span className="text-xs text-gray-500 dark:text-neutral-400">Saving...</span>
            )}
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default SynopsisCard;
